import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Send, Sparkles, Mail, Megaphone, Sun, Copy, RotateCcw, Bot, User as UserIcon,
  Volume2, VolumeX, Mic, Wand2, Check, X,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { getToken } from "@/lib/auth";
import { VoiceDictateButton, speak, stopSpeaking } from "@/components/admin/VoiceDictateButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const today = () => new Date().toISOString().slice(0, 10);

const SUB_TABS = [
  { id: "chat", label: "Chat", icon: Sparkles },
  { id: "email", label: "Draft email", icon: Mail },
  { id: "marketing", label: "Marketing copy", icon: Megaphone },
  { id: "arrivals", label: "Arrivals briefing", icon: Sun },
];

const copyToClipboard = async (text, label = "Copied") => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(label);
  } catch {
    toast.error("Could not copy.");
  }
};

/* -------------------- Chat -------------------- */
const ChatPanel = () => {
  const [sessionId] = useState(() => {
    const existing = localStorage.getItem("wl_admin_asst_session");
    if (existing) return existing;
    const fresh = `asst-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("wl_admin_asst_session", fresh);
    return fresh;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [voiceMode, setVoiceMode] = useState(() => localStorage.getItem("wl_admin_voice_mode") === "1");
  const [actionsMode, setActionsMode] = useState(() => localStorage.getItem("wl_admin_actions_mode") !== "0");
  const [pendingAction, setPendingAction] = useState(null); // { action, preview, msgIndex }
  const [executing, setExecuting] = useState(false);
  const scrollRef = useRef(null);
  const lastReplyRef = useRef("");

  useEffect(() => { localStorage.setItem("wl_admin_voice_mode", voiceMode ? "1" : "0"); }, [voiceMode]);
  useEffect(() => { localStorage.setItem("wl_admin_actions_mode", actionsMode ? "1" : "0"); }, [actionsMode]);
  useEffect(() => () => stopSpeaking(), []);

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/assistant/chat/history`, { params: { session_id: sessionId } });
      setMessages(data.messages || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const reset = async () => {
    try {
      await axios.delete(`${API}/admin/assistant/chat/history`, { params: { session_id: sessionId } });
      setMessages([]);
      stopSpeaking();
      toast.success("Conversation cleared.");
    } catch {
      toast.error("Reset failed.");
    }
  };

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || streaming || executing) return;
    setInput("");
    stopSpeaking();
    setPendingAction(null);

    // Actions mode: try to parse an actionable intent first. If GPT proposes
    // an action, show a confirmation card and skip the streaming chat entirely.
    if (actionsMode) {
      setMessages((m) => [...m, { role: "user", content: text }]);
      setExecuting(true);
      try {
        const { data } = await axios.post(`${API}/admin/assistant/intent`, { message: text });
        setMessages((m) => [...m, { role: "assistant", content: data.reply || "…" }]);
        if (voiceMode && data.reply) speak(data.reply);
        if (data.action && data.preview) {
          setPendingAction({ action: data.action, preview: data.preview });
        }
        return;
      } catch (e) {
        toast.error("Couldn't understand that — try again.");
        setMessages((m) => m.slice(0, -1));
        return;
      } finally {
        setExecuting(false);
      }
    }

    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    lastReplyRef.current = "";

    try {
      const res = await fetch(`${API}/admin/assistant/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const payload = JSON.parse(line.slice(5).trim());
            if (payload.delta) {
              lastReplyRef.current += payload.delta;
              setMessages((m) => {
                const next = [...m];
                next[next.length - 1] = { role: "assistant", content: (next[next.length - 1].content || "") + payload.delta };
                return next;
              });
            } else if (payload.error) {
              toast.error(payload.error);
            }
          } catch { /* ignore bad frames */ }
        }
      }
      if (voiceMode && lastReplyRef.current.trim()) {
        speak(lastReplyRef.current);
      }
    } catch (e) {
      toast.error("Chat failed.");
      setMessages((m) => m.slice(0, -1));
    } finally {
      setStreaming(false);
    }
  };

  const confirmAction = async () => {
    if (!pendingAction || executing) return;
    setExecuting(true);
    try {
      const { data } = await axios.post(`${API}/admin/assistant/execute`, pendingAction.action);
      let summary = "Done.";
      if (data.action === "create_block") {
        summary = data.created ? `Block created for ${pendingAction.action.unit_id}.` : "Already blocked — nothing changed.";
      } else if (data.action === "bulk_block_category") {
        summary = `Blocked ${data.created} of ${data.total_units} units (${data.skipped} already blocked).`;
      } else if (data.action === "bulk_clear_category") {
        summary = `Cleared ${data.deleted} blocks in ${data.category}.`;
      } else if (data.action === "create_booking") {
        summary = `Booking ${data.booking_id} created for £${data.total_gbp}${data.email_placeholder ? " — remember to add the guest's email." : "."}`;
      }
      setMessages((m) => [...m, { role: "assistant", content: `✓ ${summary}` }]);
      if (voiceMode) speak(summary);
      toast.success(summary);
      setPendingAction(null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't apply that.");
    } finally {
      setExecuting(false);
    }
  };

  const cancelAction = () => {
    setPendingAction(null);
    setMessages((m) => [...m, { role: "assistant", content: "Cancelled." }]);
  };

  return (
    <div className="bg-white border border-[#DDD1BE] rounded-2xl overflow-hidden" data-testid="assistant-chat-panel">
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#EDE3D2] bg-[#FAF5EC] flex-wrap gap-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#6A5F58] font-brand">
          <Bot size={14} className="text-[#B76E79]" /> GPT 5.6 Sol · Private admin chat
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setActionsMode((v) => !v)}
            data-testid="assistant-chat-actionsmode"
            className={`inline-flex items-center gap-1.5 text-xs font-brand tracking-[0.14em] uppercase link-underline ${actionsMode ? "text-[#B76E79]" : "text-[#6A5F58] hover:text-[#0F0E0E]"}`}
            title={actionsMode ? "Actions on — I'll block, clear and manage availability. Tap to switch to plain chat." : "Actions off — plain chat only. Tap to enable direct availability control."}
          >
            <Wand2 size={12} /> {actionsMode ? "Actions on" : "Actions off"}
          </button>
          <button
            type="button"
            onClick={() => { const next = !voiceMode; setVoiceMode(next); if (!next) stopSpeaking(); }}
            data-testid="assistant-chat-voicemode"
            className={`inline-flex items-center gap-1.5 text-xs font-brand tracking-[0.14em] uppercase link-underline ${voiceMode ? "text-[#B76E79]" : "text-[#6A5F58] hover:text-[#0F0E0E]"}`}
            title={voiceMode ? "Voice replies on — tap to mute" : "Voice replies off — tap to hear replies aloud"}
          >
            {voiceMode ? <Volume2 size={12} /> : <VolumeX size={12} />} {voiceMode ? "Voice on" : "Voice off"}
          </button>
          <button
            type="button"
            onClick={reset}
            data-testid="assistant-chat-reset"
            className="inline-flex items-center gap-1.5 text-xs text-[#6A5F58] hover:text-[#0F0E0E] link-underline font-brand tracking-[0.14em] uppercase"
          >
            <RotateCcw size={12} /> Reset
          </button>
        </div>
      </div>

      <div ref={scrollRef} className="h-[420px] overflow-y-auto px-5 py-6 bg-[#FCFAF4] space-y-4" data-testid="assistant-chat-log">
        {messages.length === 0 && (
          <div className="text-center text-sm text-[#6A5F58] mt-16">
            <Sparkles size={22} className="mx-auto text-[#B76E79] mb-3" />
            <p className="max-w-sm mx-auto leading-relaxed">
              Ask anything — draft a policy, summarise a week's takings, brainstorm a promo idea, or workshop a difficult guest reply.
            </p>
            <p className="max-w-sm mx-auto leading-relaxed mt-3 text-xs">
              Tap the <Mic size={11} className="inline mb-0.5" /> mic to speak hands-free. With <span className="text-[#B76E79]">Actions on</span> try <em>"block Kingfisher this weekend"</em> or <em>"book Kingfisher for Alan next Friday, 3 nights"</em>. Turn <span className="text-[#B76E79]">Voice on</span> to hear replies aloud.
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${m.role === "user" ? "bg-[#0F0E0E] text-[#F5EFE6]" : "bg-[#B76E79] text-white"}`}>
              {m.role === "user" ? <UserIcon size={14} /> : <Bot size={14} />}
            </div>
            <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#0F0E0E] text-[#F5EFE6]" : "bg-white border border-[#EDE3D2] text-[#14100F]"}`}>
              {m.content || (streaming && i === messages.length - 1 ? <Loader2 size={14} className="animate-spin inline" /> : "")}
            </div>
          </div>
        ))}
        {pendingAction && (
          <div className="ml-11 max-w-[75%] rounded-2xl border-2 border-[#B76E79] bg-[#FFF7F3] p-4 shadow-sm" data-testid="assistant-action-card">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#B76E79] font-brand">
              <Wand2 size={12} /> Confirm action
            </div>
            <div className="mt-2 text-sm text-[#14100F] font-medium">{pendingAction.preview}</div>
            {pendingAction.action?.reason && pendingAction.action.type !== "create_booking" && (
              <div className="text-xs text-[#6A5F58] mt-1">Reason: {pendingAction.action.reason}</div>
            )}
            {pendingAction.action?.type === "create_booking" && !pendingAction.action?.guest_email && (
              <div className="text-xs text-[#B76E79] mt-2 flex items-start gap-1.5">
                <span>⚠</span> No email captured — the booking will be created with a placeholder you can update later.
              </div>
            )}
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={confirmAction}
                disabled={executing}
                data-testid="assistant-action-confirm"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#B76E79] text-white text-xs font-brand tracking-[0.14em] uppercase disabled:opacity-50 hover:bg-[#a5606a]"
              >
                {executing ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Confirm
              </button>
              <button
                type="button"
                onClick={cancelAction}
                disabled={executing}
                data-testid="assistant-action-cancel"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#DDD1BE] text-[#6A5F58] text-xs font-brand tracking-[0.14em] uppercase hover:text-[#0F0E0E]"
              >
                <X size={12} /> Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-[#EDE3D2] p-3 bg-white flex gap-2 items-center">
        <VoiceDictateButton
          size="md"
          testId="assistant-chat-mic"
          onTranscript={(t) => {
            // In voice mode, auto-send. Otherwise, drop into the input for review.
            if (voiceMode) send(t);
            else setInput((prev) => (prev ? `${prev} ${t}` : t));
          }}
        />
        <Input
          data-testid="assistant-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={actionsMode ? "Try \"book Kingfisher for Alan next Friday, 3 nights\"" : voiceMode ? "Speak or type — replies will play aloud" : "Message the assistant, or tap the mic to dictate…"}
          disabled={streaming || executing}
          className="flex-1 bg-[#FAF5EC] border-[#DDD1BE]"
        />
        <button
          type="button"
          onClick={() => send()}
          disabled={streaming || executing || !input.trim()}
          data-testid="assistant-chat-send"
          className="btn-primary font-brand text-xs tracking-[0.14em] uppercase inline-flex items-center gap-2 disabled:opacity-50"
        >
          {streaming || executing ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send
        </button>
      </div>
    </div>
  );
};

/* -------------------- Draft Email -------------------- */
const EmailPanel = () => {
  const [inquiry, setInquiry] = useState("");
  const [tone, setTone] = useState("warm");
  const [context, setContext] = useState("");
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null);

  const generate = async () => {
    if (!inquiry.trim()) { toast.error("Paste the guest's enquiry first."); return; }
    setLoading(true);
    setDraft(null);
    try {
      const { data } = await axios.post(`${API}/admin/assistant/draft-email`, { inquiry, tone, context: context || null });
      setDraft(data);
    } catch { toast.error("Could not draft the email."); }
    finally { setLoading(false); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="assistant-email-panel">
      <div className="bg-white border border-[#DDD1BE] rounded-2xl p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-serif text-xl">Paste the guest enquiry</h3>
          <VoiceDictateButton
            size="sm"
            testId="assistant-email-mic"
            hint="Dictate the enquiry"
            onTranscript={(t) => setInquiry((prev) => (prev ? `${prev} ${t}` : t))}
          />
        </div>
        <Textarea
          data-testid="assistant-email-inquiry"
          value={inquiry}
          onChange={(e) => setInquiry(e.target.value)}
          rows={9}
          placeholder="Hi, do you take dogs? We'd love a hardstanding pitch for 3 nights in October…"
          className="bg-[#FAF5EC] border-[#DDD1BE]"
        />
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">Tone</Label>
            <select
              data-testid="assistant-email-tone"
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              className="w-full mt-1 border border-[#DDD1BE] rounded-md px-3 py-2 text-sm bg-[#FAF5EC]"
            >
              <option value="warm">Warm</option>
              <option value="formal">Formal</option>
              <option value="apologetic">Apologetic</option>
              <option value="enthusiastic">Enthusiastic</option>
            </select>
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">Notes for the AI (optional)</Label>
          <Input
            data-testid="assistant-email-context"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="e.g. offer 10% off next stay"
            className="mt-1 bg-[#FAF5EC] border-[#DDD1BE]"
          />
        </div>
        <button
          type="button"
          onClick={generate}
          disabled={loading}
          data-testid="assistant-email-generate"
          className="btn-primary font-brand text-xs tracking-[0.14em] uppercase inline-flex items-center gap-2 disabled:opacity-50"
        >
          {loading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Draft reply
        </button>
      </div>

      <div className="bg-white border border-[#DDD1BE] rounded-2xl p-6 space-y-4 min-h-[300px]" data-testid="assistant-email-output">
        {!draft && !loading && (
          <div className="text-sm text-[#6A5F58] text-center mt-16 max-w-xs mx-auto">
            <Mail size={22} className="mx-auto text-[#B76E79] mb-3" />
            Your draft will appear here — copy straight into Stackmail.
          </div>
        )}
        {loading && (
          <div className="text-sm text-[#6A5F58] text-center mt-16">
            <Loader2 size={22} className="mx-auto text-[#B76E79] mb-3 animate-spin" />
            Drafting…
          </div>
        )}
        {draft && (
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">Subject</Label>
                <button
                  type="button"
                  onClick={() => copyToClipboard(draft.subject, "Subject copied")}
                  className="text-xs text-[#6A5F58] hover:text-[#B76E79] inline-flex items-center gap-1"
                  data-testid="assistant-email-copy-subject"
                >
                  <Copy size={12} /> Copy
                </button>
              </div>
              <div className="mt-1 p-3 rounded-md bg-[#FAF5EC] border border-[#EDE3D2] text-sm">{draft.subject}</div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">Body</Label>
                <button
                  type="button"
                  onClick={() => copyToClipboard(draft.body, "Body copied")}
                  className="text-xs text-[#6A5F58] hover:text-[#B76E79] inline-flex items-center gap-1"
                  data-testid="assistant-email-copy-body"
                >
                  <Copy size={12} /> Copy
                </button>
              </div>
              <div className="mt-1 p-3 rounded-md bg-[#FAF5EC] border border-[#EDE3D2] text-sm whitespace-pre-wrap leading-relaxed">{draft.body}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* -------------------- Marketing -------------------- */
const MarketingPanel = () => {
  const [topic, setTopic] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [tone, setTone] = useState("warm-luxury");
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState(null);

  const generate = async () => {
    if (!topic.trim()) { toast.error("Add a topic first."); return; }
    setLoading(true);
    setOutput(null);
    try {
      const { data } = await axios.post(`${API}/admin/assistant/marketing`, { topic, platform, tone });
      setOutput(data);
    } catch { toast.error("Could not generate copy."); }
    finally { setLoading(false); }
  };

  const fullPost = output ? [output.caption, (output.hashtags || []).join(" ")].filter(Boolean).join("\n\n") : "";

  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="assistant-marketing-panel">
      <div className="bg-white border border-[#DDD1BE] rounded-2xl p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-serif text-xl">What are we posting about?</h3>
          <VoiceDictateButton
            size="sm"
            testId="assistant-marketing-mic"
            hint="Dictate the topic"
            onTranscript={(t) => setTopic((prev) => (prev ? `${prev} ${t}` : t))}
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">Topic</Label>
          <Textarea
            data-testid="assistant-marketing-topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            rows={4}
            placeholder="Autumn hardstanding pitch availability — fishing included"
            className="mt-1 bg-[#FAF5EC] border-[#DDD1BE]"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">Platform</Label>
            <select
              data-testid="assistant-marketing-platform"
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="w-full mt-1 border border-[#DDD1BE] rounded-md px-3 py-2 text-sm bg-[#FAF5EC]"
            >
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
              <option value="newsletter">Newsletter</option>
              <option value="website">Website blurb</option>
            </select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">Tone</Label>
            <select
              data-testid="assistant-marketing-tone"
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              className="w-full mt-1 border border-[#DDD1BE] rounded-md px-3 py-2 text-sm bg-[#FAF5EC]"
            >
              <option value="warm-luxury">Warm luxury</option>
              <option value="playful">Playful</option>
              <option value="practical">Practical</option>
              <option value="seasonal">Seasonal</option>
            </select>
          </div>
        </div>
        <button
          type="button"
          onClick={generate}
          disabled={loading}
          data-testid="assistant-marketing-generate"
          className="btn-primary font-brand text-xs tracking-[0.14em] uppercase inline-flex items-center gap-2 disabled:opacity-50"
        >
          {loading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Write copy
        </button>
      </div>

      <div className="bg-white border border-[#DDD1BE] rounded-2xl p-6 space-y-4 min-h-[300px]" data-testid="assistant-marketing-output">
        {!output && !loading && (
          <div className="text-sm text-[#6A5F58] text-center mt-16 max-w-xs mx-auto">
            <Megaphone size={22} className="mx-auto text-[#B76E79] mb-3" />
            Your caption, hashtags and two alternates will appear here.
          </div>
        )}
        {loading && (
          <div className="text-sm text-[#6A5F58] text-center mt-16">
            <Loader2 size={22} className="mx-auto text-[#B76E79] mb-3 animate-spin" />
            Writing…
          </div>
        )}
        {output && (
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">Caption</Label>
                <button
                  type="button"
                  onClick={() => copyToClipboard(fullPost, "Post copied")}
                  className="text-xs text-[#6A5F58] hover:text-[#B76E79] inline-flex items-center gap-1"
                  data-testid="assistant-marketing-copy-post"
                >
                  <Copy size={12} /> Copy whole post
                </button>
              </div>
              <div className="mt-1 p-3 rounded-md bg-[#FAF5EC] border border-[#EDE3D2] text-sm whitespace-pre-wrap">{output.caption}</div>
            </div>
            {(output.hashtags || []).length > 0 && (
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">Hashtags</Label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {output.hashtags.map((h, i) => (
                    <span key={i} className="text-xs px-2 py-1 rounded-full bg-[#F5EFE6] border border-[#DDD1BE] text-[#14100F]">{h}</span>
                  ))}
                </div>
              </div>
            )}
            {(output.alternates || []).length > 0 && (
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">Alternates</Label>
                <div className="mt-1 space-y-2">
                  {output.alternates.map((a, i) => (
                    <div key={i} className="p-3 rounded-md bg-[#FAF5EC] border border-[#EDE3D2] text-sm flex items-start justify-between gap-2">
                      <span className="whitespace-pre-wrap">{a}</span>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(a, "Alternate copied")}
                        className="text-xs text-[#6A5F58] hover:text-[#B76E79] flex-shrink-0"
                        aria-label="Copy alternate"
                      >
                        <Copy size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* -------------------- Arrivals Briefing -------------------- */
const ArrivalsBriefingPanel = () => {
  const [date, setDate] = useState(today());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const generate = async () => {
    setLoading(true);
    setData(null);
    try {
      const { data } = await axios.post(`${API}/admin/assistant/arrivals-summary`, { date });
      setData(data);
    } catch { toast.error("Could not generate briefing."); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-white border border-[#DDD1BE] rounded-2xl p-6 space-y-6 max-w-3xl" data-testid="assistant-arrivals-panel">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">Date</Label>
          <Input
            data-testid="assistant-arrivals-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="mt-1 bg-[#FAF5EC] border-[#DDD1BE]"
          />
        </div>
        <button
          type="button"
          onClick={generate}
          disabled={loading}
          data-testid="assistant-arrivals-generate"
          className="btn-primary font-brand text-xs tracking-[0.14em] uppercase inline-flex items-center gap-2 disabled:opacity-50"
        >
          {loading ? <Loader2 size={13} className="animate-spin" /> : <Sun size={13} />} Write briefing
        </button>
      </div>

      {loading && (
        <div className="text-sm text-[#6A5F58] text-center py-16">
          <Loader2 size={22} className="mx-auto text-[#B76E79] mb-3 animate-spin" />
          Reading today's diary…
        </div>
      )}
      {data && (
        <div className="border-t border-[#EDE3D2] pt-6 space-y-4">
          <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">
            <span>{data.arrivals_count} arrivals · {data.departures_count} departures</span>
            <button
              type="button"
              onClick={() => copyToClipboard(data.briefing, "Briefing copied")}
              className="hover:text-[#B76E79] inline-flex items-center gap-1"
              data-testid="assistant-arrivals-copy"
            >
              <Copy size={12} /> Copy
            </button>
          </div>
          <div className="p-5 rounded-xl bg-[#FAF5EC] border border-[#EDE3D2] text-sm leading-relaxed whitespace-pre-wrap font-serif text-[#14100F]" data-testid="assistant-arrivals-briefing">
            {data.briefing}
          </div>
        </div>
      )}
    </div>
  );
};

/* -------------------- Container -------------------- */
const AdminAssistant = () => {
  const [sub, setSub] = useState("chat");

  return (
    <section className="space-y-6" data-testid="admin-assistant">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="overline text-[#B76E79]"><span className="marker-line marker-line--rose" /> CRM · assistant</p>
          <h2 className="font-serif text-3xl md:text-4xl mt-4 text-[#14100F]">AI assistant</h2>
          <p className="text-sm text-[#6A5F58] mt-2 max-w-xl">
            Your private assistant powered by GPT 5.6 Sol. Draft replies, write posts, and turn today's diary into a friendly briefing.
          </p>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap border-b border-[#DDD1BE]">
        {SUB_TABS.map((t) => {
          const Icon = t.icon;
          const active = sub === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setSub(t.id)}
              data-testid={`assistant-subtab-${t.id}`}
              className={`inline-flex items-center gap-2 py-3 px-1 text-sm font-brand tracking-[0.14em] uppercase border-b-2 transition ${
                active ? "border-[#B76E79] text-[#0F0E0E]" : "border-transparent text-[#6A5F58] hover:text-[#0F0E0E]"
              }`}
            >
              <Icon size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      {sub === "chat" && <ChatPanel />}
      {sub === "email" && <EmailPanel />}
      {sub === "marketing" && <MarketingPanel />}
      {sub === "arrivals" && <ArrivalsBriefingPanel />}
    </section>
  );
};

export default AdminAssistant;
